import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { orderDocumentsAPI } from '../../lib/api';
import { GoogleMapsProvider } from '../../components/GoogleMapsProvider';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import {
  Loader2, ShieldCheck, UploadCloud, Check, X, FileText, CreditCard,
  BarChart3, IdCard, Landmark, AlertCircle, PenTool,
} from 'lucide-react';

const LEVEL_META = {
  requis:     { label: 'Requis',      cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  recommande: { label: 'Recommandé',  cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  optionnel:  { label: 'Optionnel',   cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};
const DOC_ICON = {
  kbis: FileText, rib: CreditCard, dernier_bilan_et_compte_de_resultat: BarChart3,
  piece_identite: IdCard, pouvoir_signature: PenTool,
};
const DOC_HINT = {
  pouvoir_signature: "À fournir si le signataire n'est pas le représentant légal de la société.",
};

function DocCard({ doc, file, onPick, onClear }) {
  const Icon = DOC_ICON[doc.code] || FileText;
  const lvl = LEVEL_META[doc.level] || LEVEL_META.optionnel;
  const done = !!file;
  const validated = doc.status === 'valide';
  const rejected = doc.status === 'refuse';
  const existant = doc.status === 'existant';
  const badge = validated
    ? { txt: 'Validé', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
    : done
    ? { txt: 'Fourni', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
    : existant
    ? { txt: 'Déjà fourni', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
    : rejected
    ? { txt: 'À renvoyer', cls: 'bg-red-50 text-red-700 border-red-200' }
    : { txt: lvl.label, cls: lvl.cls };
  return (
    <div className={`rounded-xl border p-4 bg-white transition ${validated ? 'border-emerald-300 opacity-80' : done ? 'border-emerald-400' : rejected ? 'border-red-200' : 'border-gray-200'}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center shrink-0">
          <Icon className="w-[18px] h-[18px] text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{doc.nom}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.txt}</span>
          </div>
          {rejected && doc.motif && <div className="text-xs text-red-600 mt-1">Motif : {doc.motif}</div>}
          {existant && !done && <div className="text-xs text-gray-500 mt-1">Déjà en notre possession — déposez un fichier seulement si vous souhaitez le mettre à jour.</div>}
          {DOC_HINT[doc.code] && !done && <div className="text-xs text-gray-500 mt-1">{DOC_HINT[doc.code]}</div>}
        </div>
      </div>
      {validated ? (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2.5 text-[13px] text-emerald-700">
          <Check className="w-4 h-4" /> Pièce validée — rien à faire
        </div>
      ) : (
        <label className={`mt-3 flex items-center gap-3 rounded-lg border-[1.5px] border-dashed px-3 py-3 cursor-pointer transition
          ${done ? 'border-emerald-400 bg-emerald-50/60' : 'border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50/40'}`}>
          {done ? <Check className="w-5 h-5 text-emerald-600 shrink-0" /> : <UploadCloud className="w-5 h-5 text-gray-400 shrink-0" />}
          <span className="text-[13px] text-gray-600 flex-1 truncate">
            {done ? file.name : <><b className="text-emerald-700">Parcourir</b> ou déposer votre fichier</>}
          </span>
          {done && (
            <button type="button" onClick={(e) => { e.preventDefault(); onClear(); }} className="text-gray-400 hover:text-red-500">
              <X className="w-4 h-4" />
            </button>
          )}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
            onChange={(e) => e.target.files[0] && onPick(e.target.files[0])} />
        </label>
      )}
    </div>
  );
}

export default function OrderDocumentsFormPage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | error | done
  const [cfg, setCfg] = useState(null);
  const [files, setFiles] = useState({});        // code -> File
  const [sepa, setSepa] = useState({ banque: '', iban: '', bic: '' });
  const [sig, setSig] = useState({ nom: '', prenom: '', fonction: '', tel: '', email: '' });
  const [siren, setSiren] = useState('');
  const [adresse, setAdresse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [logo, setLogo] = useState('');
  const [error, setError] = useState(null);
  const [sendError, setSendError] = useState('');
  const [editInfos, setEditInfos] = useState(false); // expand already-saved info on a re-send
  const [modifyBank, setModifyBank] = useState(false); // client wants to change bank details already on file
  const [modifySig, setModifySig] = useState(false);   // client wants to change the signatory already on file

  useEffect(() => {
    orderDocumentsAPI.getForm(token)
      .then(r => {
        const d = r.data;
        setCfg(d);
        setState(d.already_submitted ? 'done' : 'ready');
        // Prefill signatory / delivery address / bank details already provided in
        // a previous submission so a re-send never forces the client to retype them.
        const s = d.signataire || {};
        if (s.nom || s.prenom || s.email) {
          setSig({
            nom: s.nom || '', prenom: s.prenom || '', fonction: s.fonction || '',
            tel: s.telephone || '', email: s.email || '',
          });
        }
        if (d.siren) setSiren(d.siren);
        if (d.adresse_livraison) setAdresse(d.adresse_livraison);
        // A bank-only link asks for BRAND NEW coordinates (the old ones are
        // shown separately, read-only, for reference) — never prefill the
        // editable fields with them, or the client could submit unchanged.
        const sv = d.sepa_saved || {};
        if (!d.bank_only && (sv.banque || sv.iban || sv.bic)) {
          setSepa({ banque: sv.banque || '', iban: sv.iban || '', bic: sv.bic || '' });
        }
      })
      .catch(() => setState('error'));
    orderDocumentsAPI.getBranding()
      .then(r => {
        const raw = r.data?.logo_url;
        if (raw) setLogo(raw.startsWith('http') ? raw : `${process.env.REACT_APP_BACKEND_URL || ''}${raw}`);
      })
      .catch(() => {});
  }, [token]);

  // "Pouvoir de signature" is shown under the signatory block, not in the main list.
  // Every requested document — including the "pouvoir" — is shown in the main
  // list so it's uploadable directly (never hidden inside a collapsed section).
  const mainDocs = useMemo(() => (cfg?.requested || []), [cfg]);
  // Only show the documents that still need action: to re-send (rejected) or to
  // provide (missing). Already-validated / already-on-file pieces are hidden.
  const isSettled = (d) => d.status === 'valide' || d.status === 'existant';
  const visibleDocs = useMemo(() => mainDocs.filter(d => !isSettled(d)), [mainDocs]);
  const reqDocs = useMemo(() => mainDocs.filter(d => d.level === 'requis'), [mainDocs]);
  const reqDone = reqDocs.filter(d => files[d.code] || d.status === 'existant' || d.status === 'valide').length;
  // RIB carries its real status (from the requested config) so an already-validated
  // RIB never blocks the form and is not shown again.
  const ribInfo = useMemo(() => (cfg?.requested || []).find(d => d.code === 'rib'), [cfg]);
  const ribRequired = cfg?.sepa;
  const ribSettled = ribInfo && (ribInfo.status === 'valide' || ribInfo.status === 'existant');
  const ribDone = !!files.rib || ribSettled;
  // Coordonnées bancaires déjà en notre possession (import HubSpot / commande
  // précédente) : on ne les redemande PAS. On ne verrouille que si le mandat est
  // COMPLET (banque + IBAN + BIC) — un enregistrement partiel doit rester à
  // compléter. La ressaisie ne sert qu'au changement de banque (« Modifier »).
  const sv = cfg?.sepa_saved || {};
  const sepaOnFile = !cfg?.bank_only && !!(sv.banque?.trim() && sv.iban?.trim() && sv.bic?.trim());
  const bankLocked = sepaOnFile && !modifyBank;
  // Signataire déjà défini : on ne le fait pas ressaisir — mais seulement si TOUS
  // les champs requis sont présents (sinon les PDF auraient des champs vides).
  const sg = cfg?.signataire || {};
  const sigOnFile = !!(sg.prenom?.trim() && sg.nom?.trim() && sg.fonction?.trim() && sg.telephone?.trim() && sg.email?.trim());
  const sigLocked = sigOnFile && !modifySig;
  // On a re-send, the signatory / bank / address were already captured: collapse
  // those sections behind a "modifier" affordance instead of asking again.
  const infosSaved = !!(cfg?.is_resubmission && (cfg?.signataire?.email || cfg?.signataire?.nom));

  const up = (v) => v.toUpperCase();
  const cap = (v) => v.replace(/\b\p{L}/gu, c => c.toUpperCase());
  const sentence = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : v);

  const isDone = (d) => files[d.code] || d.status === 'existant' || d.status === 'valide';

  const validate = () => {
    const missing = [];
    if (cfg.bank_only) {
      // A bank-details-only link only asks for the new banking info + RIB.
      if (!sepa.banque.trim()) missing.push('Banque');
      if (!sepa.iban.trim()) missing.push('IBAN');
      if (!sepa.bic.trim()) missing.push('BIC');
      if (!files.rib) missing.push('RIB');
      return missing;
    }
    // Required documents
    mainDocs.filter(d => d.level === 'requis').forEach(d => { if (!isDone(d)) missing.push(d.nom); });
    // SEPA (only when prélèvement). Si les coordonnées sont déjà en notre
    // possession et que le client ne demande pas à les modifier, on ne redemande
    // ni les champs ni le RIB.
    if (cfg.sepa && !bankLocked) {
      if (!sepa.banque.trim()) missing.push('Banque');
      if (!sepa.iban.trim()) missing.push('IBAN');
      if (!sepa.bic.trim()) missing.push('BIC');
      if (ribRequired && !ribDone) missing.push('RIB');
    }
    // Signatory (all fields) — skipped when already defined and not being changed.
    if (!sigLocked) {
      if (!sig.nom.trim()) missing.push('Nom du signataire');
      if (!sig.prenom.trim()) missing.push('Prénom du signataire');
      if (!sig.fonction.trim()) missing.push('Fonction du signataire');
      if (!sig.tel.trim()) missing.push('Téléphone du signataire');
      if (!/^\S+@\S+\.\S+$/.test(sig.email.trim())) missing.push('E-mail du signataire');
    }
    // SIREN — exactly 9 digits
    if (!/^\d{9}$/.test(siren.trim())) missing.push('SIREN (9 chiffres)');
    // Delivery address
    if (!adresse.trim()) missing.push('Adresse de livraison');
    return missing;
  };

  const submit = async () => {
    const missing = validate();
    if (missing.length) {
      setError(missing);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError(null);
    setSendError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(files).forEach(([code, f]) => { fd.append('codes', code); fd.append('files', f); });
      fd.append('banque', sepa.banque); fd.append('iban', sepa.iban); fd.append('bic', sepa.bic);
      fd.append('signataire_nom', sig.nom); fd.append('signataire_prenom', sig.prenom);
      fd.append('signataire_fonction', sig.fonction); fd.append('signataire_tel', sig.tel);
      fd.append('signataire_email', sig.email);
      fd.append('siren', siren);
      fd.append('adresse_livraison', adresse);
      await orderDocumentsAPI.submitForm(token, fd);
      setState('done');
    } catch (e) {
      Sentry.captureException(e);
      // Surface the REAL cause so it can be relayed instead of a generic message.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      const parts = [];
      if (status) parts.push(`HTTP ${status}`);
      if (detail) parts.push(typeof detail === 'string' ? detail : JSON.stringify(detail));
      if (!status && e?.code) parts.push(e.code); // e.g. ERR_NETWORK / ECONNABORTED (timeout)
      if (!parts.length) parts.push(e?.message || 'Erreur inconnue');
      setSendError(parts.join(' — '));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'loading') {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  }
  if (state === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-center px-6">
        <AlertCircle className="w-12 h-12 text-gray-300 mb-3" />
        <h1 className="text-lg font-semibold text-gray-800">Lien invalide ou expiré</h1>
        <p className="text-sm text-gray-500 mt-1">Merci de contacter votre conseiller LDWS.</p>
      </div>
    );
  }
  if (state === 'done') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-center px-6">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <Check className="w-7 h-7 text-emerald-600" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">Documents envoyés — merci !</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-sm">Vos pièces ont bien été transmises à nos équipes. Nous revenons vers vous rapidement.</p>
      </div>
    );
  }

  return (
    <GoogleMapsProvider>
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-2xl mx-auto px-5">
        <header className="flex items-center justify-between py-6">
          {logo ? (
            <img src={logo} alt="LDWS Groupe" className="h-10 w-auto" />
          ) : (
            <div className="leading-none">
              <div className="text-[26px] font-extrabold tracking-tight text-gray-900">ldws</div>
              <div className="text-[10px] font-bold tracking-[0.35em] text-gray-400 mt-1">GROUPE</div>
            </div>
          )}
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 bg-white rounded-full px-3 py-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Transmission sécurisée
          </span>
        </header>

        <div className="rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-500 text-white p-6 sm:p-7">
          <div className="text-[11px] font-bold uppercase tracking-widest text-white/80">
            {cfg.bank_only ? 'Mise à jour bancaire' : 'Finalisation de votre commande'}
          </div>
          <h1 className="text-2xl font-extrabold mt-1.5">
            {cfg.bank_only ? 'Mettez à jour vos coordonnées bancaires' : 'Quelques documents pour finaliser votre dossier'}
          </h1>
          <p className="text-sm text-white/90 mt-1.5 max-w-md">
            {cfg.bank_only
              ? 'Merci de nous transmettre votre nouveau RIB et vos coordonnées de prélèvement à jour.'
              : 'Merci de nous transmettre les pièces ci-dessous. Cela ne prend que quelques minutes.'}
          </p>
          {cfg.company_name && <div className="mt-4 text-sm"><span className="text-white/70">Société</span><div className="font-bold">{cfg.company_name}</div></div>}
        </div>

        {!cfg.bank_only && reqDocs.length > 0 && (
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 mt-4">
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${reqDocs.length ? (reqDone / reqDocs.length) * 100 : 0}%` }} />
            </div>
            <span className="text-[13px] font-semibold text-gray-500 tabular-nums">{reqDone}/{reqDocs.length} pièces requises</span>
          </div>
        )}

        {/* Documents */}
        {!cfg.bank_only && (
        <section className="mt-6">
          <h2 className="font-bold text-gray-900 mb-3">Vos documents</h2>
          {visibleDocs.length === 0 ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-800 flex items-center gap-2">
              <Check className="w-4 h-4" /> Toutes vos pièces sont déjà en notre possession.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {visibleDocs.map(doc => (
                <DocCard key={doc.code} doc={doc}
                  file={files[doc.code]}
                  onPick={(f) => setFiles(s => ({ ...s, [doc.code]: f }))}
                  onClear={() => setFiles(s => { const n = { ...s }; delete n[doc.code]; return n; })} />
              ))}
            </div>
          )}
        </section>
        )}

        {!cfg.bank_only && infosSaved && !editInfos && (
          <section className="mt-6">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-center justify-between gap-3">
              <div className="flex items-start gap-2.5 text-sm text-emerald-800 min-w-0">
                <Check className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold">Vos informations sont déjà enregistrées</div>
                  <div className="text-xs text-emerald-700/80 mt-0.5">Signataire, coordonnées bancaires et adresse de livraison — rien à ressaisir. Il vous suffit de joindre la pièce demandée ci-dessus.</div>
                </div>
              </div>
              <button type="button" onClick={() => setEditInfos(true)} className="text-xs font-semibold text-emerald-700 underline shrink-0">Modifier</button>
            </div>
          </section>
        )}

        {(!infosSaved || editInfos) && (<>
        {/* Entreprise — SIREN (not needed for a bank-details-only update) */}
        {!cfg.bank_only && (
        <section className="mt-6">
          <h2 className="font-bold text-gray-900 mb-3">Votre entreprise</h2>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <Field label="SIREN (9 chiffres)" value={siren}
              onChange={v => setSiren(v.replace(/\D/g, '').slice(0, 9))}
              placeholder="123456789" />
          </div>
        </section>
        )}

        {/* SEPA */}
        {cfg.sepa && (
          <section className="mt-6">
            <h2 className="font-bold text-gray-900 mb-3">Coordonnées de prélèvement</h2>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="inline-flex items-center gap-2 text-[13px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                <Landmark className="w-3.5 h-3.5" /> Règlement par prélèvement SEPA
              </div>
              {/* Coordonnées déjà en notre possession : affichées en lecture
                  seule. Rien à ressaisir — on ne propose la modification que si le
                  client a changé de banque (bouton « Modifier »). */}
              {bankLocked ? (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-start justify-between gap-3">
                  <div className="text-sm text-gray-600 min-w-0">
                    <p className="font-semibold text-gray-700 mb-1">Coordonnées bancaires enregistrées</p>
                    <p className="break-words">{cfg.sepa_saved.banque || '—'} · IBAN {cfg.sepa_saved.iban || '—'} · BIC {cfg.sepa_saved.bic || '—'}</p>
                    <p className="text-xs text-gray-500 mt-1">Rien à faire — nous les avons déjà. Utilisez « Modifier » uniquement si elles ont changé.</p>
                  </div>
                  <button type="button" onClick={() => { setModifyBank(true); setSepa({ banque: '', iban: '', bic: '' }); }} className="text-xs font-semibold text-emerald-700 underline shrink-0">Modifier</button>
                </div>
              ) : (<>
              {cfg.bank_only && (cfg.sepa_saved?.banque || cfg.sepa_saved?.iban || cfg.sepa_saved?.bic) && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                  <p className="font-semibold text-gray-700 mb-1">Coordonnées actuellement enregistrées</p>
                  <p>{cfg.sepa_saved.banque || '—'} · IBAN {cfg.sepa_saved.iban || '—'} · BIC {cfg.sepa_saved.bic || '—'}</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {(cfg.bank_only || modifyBank) && (
                  <p className="sm:col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nouvelles coordonnées</p>
                )}
                <Field label="Banque" value={sepa.banque} onChange={v => setSepa(s => ({ ...s, banque: up(v) }))} placeholder="QONTO" />
                <Field label="BIC" value={sepa.bic} onChange={v => setSepa(s => ({ ...s, bic: up(v) }))} placeholder="QNTOFRP1XXX" />
                <Field className="sm:col-span-2" label="IBAN" value={sepa.iban} onChange={v => setSepa(s => ({ ...s, iban: up(v) }))} placeholder="FR76 …" />
              </div>
              {ribRequired && !ribSettled && (
                <div className="mt-3">
                  <DocCard doc={{ code: 'rib', nom: 'RIB', level: 'requis', status: ribInfo?.status, motif: ribInfo?.motif }}
                    file={files.rib}
                    onPick={(f) => setFiles(s => ({ ...s, rib: f }))}
                    onClear={() => setFiles(s => { const n = { ...s }; delete n.rib; return n; })} />
                </div>
              )}
              </>)}
            </div>
          </section>
        )}

        {/* Signataire */}
        {!cfg.bank_only && (
        <section className="mt-6">
          <h2 className="font-bold text-gray-900 mb-3">Signataire du contrat</h2>
          {sigLocked ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-start justify-between gap-3">
              <div className="text-sm text-gray-600 min-w-0">
                <p className="font-semibold text-gray-700">{[sig.prenom, sig.nom].filter(Boolean).join(' ')}</p>
                <p className="text-xs text-gray-500 mt-0.5 break-words">{[sig.fonction, sig.email, sig.tel].filter(Boolean).join(' · ')}</p>
                <p className="text-xs text-gray-500 mt-1">Signataire déjà défini — rien à ressaisir.</p>
              </div>
              <button type="button" onClick={() => setModifySig(true)} className="text-xs font-semibold text-emerald-700 underline shrink-0">Modifier</button>
            </div>
          ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Prénom" value={sig.prenom} onChange={v => setSig(s => ({ ...s, prenom: cap(v) }))} />
            <Field label="Nom" value={sig.nom} onChange={v => setSig(s => ({ ...s, nom: up(v) }))} />
            <Field label="Fonction" value={sig.fonction} onChange={v => setSig(s => ({ ...s, fonction: sentence(v) }))} />
            <Field label="Téléphone mobile" value={sig.tel} onChange={v => setSig(s => ({ ...s, tel: v }))} />
            <Field className="sm:col-span-2" label="E-mail du signataire" type="email" value={sig.email} onChange={v => setSig(s => ({ ...s, email: v }))} />
            <p className="sm:col-span-2 text-[12px] text-gray-500">Ces informations servent uniquement à l'établissement du contrat, jamais à des fins commerciales.</p>
          </div>
          )}
        </section>
        )}

        {/* Adresse */}
        {!cfg.bank_only && (
        <section className="mt-6">
          <h2 className="font-bold text-gray-900 mb-3">Adresse de livraison</h2>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <label className="text-[12px] font-semibold text-gray-500">Adresse complète</label>
            <div className="mt-1.5">
              <AddressAutocomplete
                value={adresse}
                onChange={setAdresse}
                onAddressSelect={(a) => setAdresse(a.formattedAddress || a.formatted_address || adresse)}
                placeholder="Commencez à saisir votre adresse…"
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg py-2.5 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
          </div>
        </section>
        )}
        </>)}

        <div className="mt-6 rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-[13px] text-emerald-800/90">
            <strong>Confidentialité & RGPD.</strong> Vos documents et informations sont traités de manière
            strictement confidentielle, conformément au Règlement Général sur la Protection des Données (RGPD).
            Ils sont utilisés uniquement pour la gestion de votre dossier, conservés de façon sécurisée, et
            ne sont jamais transmis à des tiers à des fins commerciales. Conformément à la réglementation,
            vous disposez d'un droit d'accès, de rectification et de suppression de vos données.
          </p>
        </div>

        {error && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-1.5">
              <AlertCircle className="w-4 h-4" /> Merci de compléter les éléments suivants :
            </div>
            <ul className="list-disc pl-6 text-sm text-red-700 space-y-0.5">
              {error.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {sendError && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-1.5">
              <AlertCircle className="w-4 h-4" /> L'envoi n'a pas abouti
            </div>
            <p className="text-sm text-red-700">Merci de réessayer. Si le problème persiste, communiquez ce détail à votre conseiller :</p>
            <p className="mt-1.5 font-mono text-xs text-red-800 bg-red-100 rounded px-2 py-1 break-all">{sendError}</p>
          </div>
        )}

        <button onClick={submit} disabled={submitting}
          className="mt-7 w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2 transition">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {cfg.bank_only ? 'Envoyer mes coordonnées bancaires' : 'Envoyer mes documents'}
        </button>
        <p className="text-center text-[12px] text-gray-500 mt-3 max-w-md mx-auto">Vos pièces sont chiffrées et accessibles uniquement par nos équipes.</p>
      </div>
    </div>
    </GoogleMapsProvider>
  );
}

function Field({ label, value, onChange, placeholder = '', type = 'text', className = '' }) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label className="text-[12px] font-semibold text-gray-500">{label}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)}
        className="text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
    </div>
  );
}
